/**
 * Chat Workspace (rechte Split-Screen-Hälfte, Kampagnen-Builder-v2-Look)
 *
 * Permanenter Arbeitsbereich neben dem Chat: Kopfzeile mit Fortschritt
 * ("x von 3 Schritten vollständig") und Schritt-Tabs mit Status-Punkten
 * (✓ / !), darunter das Formular des aktiven Schritts:
 *   1. Account-Daten (Profil + Firma, rollenabhängig)
 *   2. LinkedIn verbinden (erst Lizenz, dann Zugangsdaten — mehrere Accounts möglich)
 *   3. Arbeitszeiten (pro LinkedIn-Account)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAIChatStore from '../../../stores/ai-chat-store';
import useUserStore from '../../../stores/user-store';
import ProfileDataPanel from './profile-data-panel';
import LinkedInConnectPanel from './linkedin-connect-panel';
import WorkHoursPanel from './work-hours-panel';
import AccountLimitsPanel from './account-limits-panel';
import SignaturePanel from './signature-panel';
import LicensePanel from './license-panel';
import TeamPanel from './team-panel';
import BlacklistPanel from './blacklist-panel';
import { getUserInfo } from '../../../API/user';
import { getOwnCompanyData } from '../../../API/company';
import { getAllLinkedinAccounts } from '../../../API/linkedin-accounts';
import { API_V1 } from '../../../API/APIs/APIv1';
import { dc } from '../design-tokens';

type StepKey = 'profile' | 'team' | 'license' | 'linkedin' | 'workhours' | 'limits' | 'signature' | 'blacklist';

interface StepStatus {
  profile: boolean | null;
  team: boolean | null;
  license: boolean | null;
  linkedin: boolean | null;
  workhours: boolean | null;
  limits: boolean | null;
  signature: boolean | null;
  blacklist: boolean | null;
}

const ADMIN_STEPS: StepKey[] = ['profile', 'team', 'license', 'linkedin', 'workhours', 'limits', 'signature', 'blacklist'];
// Member sehen nur die Schritte, die sie selbst betreffen — Benutzer und
// Lizenz (und die Firmendaten in Schritt 1) verwaltet der Admin.
const MEMBER_STEPS: StepKey[] = ['profile', 'linkedin', 'workhours', 'limits', 'signature'];

interface ChatWorkspaceProps {
  /** 'inline' = Split-Screen-Spalte; 'modal' = eingebettet im Einrichtungs-Modal */
  variant?: 'inline' | 'modal';
  /** Meldet nach jedem Status-Load, ob alle Pflicht-Schritte vollständig sind. */
  onStatusChange?: (allDone: boolean) => void;
  /** false = Chat-Kontext (activeSetupStep) nicht setzen (z.B. unsichtbare Status-Probe). */
  syncContext?: boolean;
}

const ChatWorkspace: React.FC<ChatWorkspaceProps> = ({
  variant = 'inline',
  onStatusChange,
  syncContext = true,
}) => {
  const isModal = variant === 'modal';
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';

  const { sidePanel, setSidePanel, queueUserMessage, setContext, dataVersion } = useAIChatStore((state) => ({
    sidePanel: state.sidePanel,
    setSidePanel: state.setSidePanel,
    queueUserMessage: state.queueUserMessage,
    setContext: state.setContext,
    dataVersion: state.dataVersion,
  }));

  // Wizard-Flags pro Account speichern: Mit festen Schlüsseln galt eine Bestätigung im Browser
  // für jeden späteren Account — ein neuer Account übersprang z. B. den Schritt „Benutzer".
  const userId = useUserStore(
    (state) => {
      const basics = state.basics as { id?: string; _id?: string } | undefined;
      return basics?.id || basics?._id || state.details?.id || '';
    }
  );
  const flagKey = useCallback((name: string) => `${name}:${userId}`, [userId]);
  const readFlag = useCallback(
    (name: string) => typeof window !== 'undefined' && !!userId && localStorage.getItem(flagKey(name)) === '1',
    [userId, flagKey]
  );
  const writeFlag = useCallback(
    (name: string) => {
      if (userId) localStorage.setItem(flagKey(name), '1');
    },
    [userId, flagKey]
  );

  const [blacklistSkipped, setBlacklistSkipped] = useState<boolean>(() => readFlag('saia-blacklist-step-skipped'));
  // Benutzer-Schritt: erledigt erst nach ausdrücklicher Bestätigung — der eigene User existiert
  // zwar immer, aber Benutzer und geplante Lizenzen soll der Admin einmal ansehen.
  const [teamConfirmed, setTeamConfirmed] = useState<boolean>(() => readFlag('saia-team-step-confirmed'));
  // Limits haben Default-Werte und galten deshalb sofort als erledigt — nach den Arbeitszeiten
  // sprang der Wizard direkt zur Signatur. Jetzt erst, wenn die Limits einmal gespeichert wurden.
  const [limitsConfirmed, setLimitsConfirmed] = useState<boolean>(() => readFlag('saia-limits-step-confirmed'));
  // Arbeitszeiten: Beim Verbinden legt der Server Standard-Zeiten an — ohne Bestätigung
  // galt der Schritt sofort als erledigt. Erst nach einmaligem Speichern im Wizard.
  const [workhoursConfirmed, setWorkhoursConfirmed] = useState<boolean>(() =>
    readFlag('saia-workhours-step-confirmed')
  );
  // Signatur ist optional: überspringen zählt als erledigt (wie die Blacklist).
  const [signatureSkipped, setSignatureSkipped] = useState<boolean>(() => readFlag('saia-signature-step-skipped'));

  // Account gewechselt (oder User erst nach dem Mount bekannt) → Flags dieses Accounts laden
  useEffect(() => {
    setBlacklistSkipped(readFlag('saia-blacklist-step-skipped'));
    setTeamConfirmed(readFlag('saia-team-step-confirmed'));
    setLimitsConfirmed(readFlag('saia-limits-step-confirmed'));
    setWorkhoursConfirmed(readFlag('saia-workhours-step-confirmed'));
    setSignatureSkipped(readFlag('saia-signature-step-skipped'));
  }, [readFlag]);
  const [status, setStatus] = useState<StepStatus>({
    profile: null, team: null, license: null, linkedin: null,
    workhours: null, limits: null, signature: null, blacklist: null,
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);
  const bumpDataVersion = useAIChatStore((state) => state.bumpDataVersion);

  // Zurück vom Stripe-Checkout (?checkout=success): Die Lizenz legt der Webhook erst ein paar
  // Sekunden später an. Ohne Nachladen blieb der Lizenz-Schritt offen, bis die Seite neu lud.
  // Daher alle 3 s neu laden (höchstens 30 s), bis die Lizenz da ist.
  const [awaitingCheckout, setAwaitingCheckout] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('checkout') === 'success'
  );
  useEffect(() => {
    if (!awaitingCheckout) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      bumpDataVersion();
      if (tries >= 10) setAwaitingCheckout(false);
    }, 3000);
    return () => clearInterval(timer);
  }, [awaitingCheckout, bumpDataVersion]);

  // Nach jedem Chat-Turn neu laden — der Bot kann Daten geändert haben.
  useEffect(() => {
    refresh();
  }, [dataVersion, refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [user, companyRes, accountsRes, licensesRes, blacklistsRes] = await Promise.all([
          getUserInfo(),
          getOwnCompanyData(),
          getAllLinkedinAccounts(),
          API_V1.get('/licenses/current/full').catch(() => ({ data: [] })),
          API_V1.get('/blacklists').catch(() => ({ data: { blacklists: [] } })),
        ]);
        if (cancelled) return;

        const company = companyRes?.data;
        const membership = (user?.tenants || []).find(
          (m: { tenantId?: string }) => m.tenantId === company?._id
        );
        const role = membership?.role || 'member';
        const canEditCompany = role === 'admin' || role === 'owner';
        setIsAdmin(canEditCompany);

        // Bestandskunden (vor dem Wizard registriert, ohne setupWizard) gelten als eingerichtet:
        // Öffnen sie das Setup über den Chat, sind alle Schritte abgehakt.
        if (user?.setupWizard !== true) {
          setStatus({
            profile: true, team: true, license: true, linkedin: true,
            workhours: true, limits: true, signature: true, blacklist: true,
          });
          return;
        }

        const personalDone = !!(user?.firstName?.trim() && user?.lastName?.trim() && user?.email?.trim());
        const companyDone = !!(
          company?.name?.trim() &&
          company?.address?.addressLine1?.trim() &&
          company?.address?.postalCode?.trim() &&
          company?.address?.city?.trim() &&
          company?.address?.country?.trim()
        );
        // Member: Firmendaten pflegt ein Admin — für sie zählt nur der eigene Teil.
        const profileDone = canEditCompany ? personalDone && companyDone : personalDone;

        const accounts: any[] = accountsRes?.allLinkedInAccounts || [];
        const linkedinDone = accounts.length > 0;
        const workhoursDone =
          accounts.length > 0 &&
          workhoursConfirmed &&
          accounts.every((a) => (a.workDays?.length || 0) > 0 && (a.workHours?.length || 0) > 0);

        const limitsDone = accounts.length > 0 && limitsConfirmed;
        const signatureDone =
          accounts.length > 0 &&
          (signatureSkipped || accounts.every((a) => (a.signature || '').trim().length > 0));

        const saiaLicenses = ((licensesRes as any)?.data || []).filter(
          (l: any) => ['saia_recruiting', 'saia_sales'].includes(l.product) && !l.isExpired
        );
        const licenseDone = canEditCompany ? saiaLicenses.length > 0 : true;
        const teamDone = teamConfirmed;
        // Blacklist-Schritt ist optional: erledigt, wenn eine Liste existiert
        // ODER der Admin ihn bewusst übersprungen hat.
        const blacklistCount = ((blacklistsRes as any)?.data?.blacklists || []).length;
        const blacklistDone = blacklistCount > 0 || blacklistSkipped;

        setStatus({
          profile: profileDone, team: teamDone, license: licenseDone, linkedin: linkedinDone,
          workhours: workhoursDone, limits: limitsDone, signature: signatureDone, blacklist: blacklistDone,
        });
      } catch {
        if (!cancelled) {
          setStatus({
            profile: null, team: null, license: null, linkedin: null,
            workhours: null, limits: null, signature: null, blacklist: null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick, blacklistSkipped, teamConfirmed, limitsConfirmed, workhoursConfirmed, signatureSkipped]);

  useEffect(() => {
    if (awaitingCheckout && status.license === true) {
      setAwaitingCheckout(false);
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }, [awaitingCheckout, status.license]);

  const stepOrder = isAdmin ? ADMIN_STEPS : MEMBER_STEPS;
  const doneCount = stepOrder.filter((k) => status[k] === true).length;
  const statusLoaded = stepOrder.every((k) => status[k] !== null);
  const firstOpen = stepOrder.find((k) => status[k] === false);
  const allDone = doneCount === stepOrder.length;
  const activeStep: StepKey | 'done' =
    sidePanel && stepOrder.includes(sidePanel as StepKey)
      ? (sidePanel as StepKey)
      : firstOpen || 'done';

  // Modal-Wrapper über den Vollständigkeits-Status informieren
  useEffect(() => {
    if (statusLoaded && onStatusChange) onStatusChange(allDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusLoaded, allDone]);

  // Kein automatischer Sprung zu „Fertig", sobald alles erledigt ist: der übersprang nach den
  // Arbeitszeiten Limits, Signatur und Blacklist, wenn die schon erledigt waren. „Weiter" geht
  // immer genau einen Schritt weiter.

  const stepLabels: Record<StepKey, string> = {
    profile: isDe ? 'Account-Daten' : 'Account details',
    team: isDe ? 'Benutzer' : 'Users',
    license: isDe ? 'Lizenz' : 'License',
    linkedin: 'LinkedIn',
    workhours: isDe ? 'Arbeitszeiten' : 'Working hours',
    limits: isDe ? 'Limits' : 'Limits',
    signature: isDe ? 'Signatur' : 'Signature',
    blacklist: isDe ? 'Blacklists' : 'Blacklists',
  };

  // Schritt nach dem aktiven; nach dem letzten kommt die Fertig-Ansicht.
  const nextIndex = activeStep === 'done' ? -1 : stepOrder.indexOf(activeStep) + 1;
  const nextStep = (
    activeStep === 'done' ? null : nextIndex < stepOrder.length ? stepOrder[nextIndex] : 'done'
  ) as StepKey | 'done' | null;
  const goNext = useCallback(() => {
    if (nextStep) setSidePanel(nextStep === 'done' ? null : nextStep);
  }, [nextStep, setSidePanel]);

  const handleTeamConfirm = useCallback(() => {
    writeFlag('saia-team-step-confirmed');
    setTeamConfirmed(true);
    goNext();
  }, [goNext, writeFlag]);

  const handleSignatureSkip = useCallback(() => {
    writeFlag('saia-signature-step-skipped');
    setSignatureSkipped(true);
    goNext();
  }, [goNext, writeFlag]);

  const handleBlacklistSkip = useCallback(() => {
    writeFlag('saia-blacklist-step-skipped');
    setBlacklistSkipped(true);
    goNext();
  }, [goNext, writeFlag]);

  // Der Chat weiß immer, welcher Tab offen ist — geht als Kontext mit jeder Nachricht mit.
  useEffect(() => {
    if (syncContext) setContext({ activeSetupStep: activeStep });
  }, [activeStep, setContext, syncContext]);

  const handleSaved = useCallback(
    (summary: string) => {
      queueUserMessage(summary);
      refresh();
    },
    [queueUserMessage, refresh]
  );

  const handleWorkhoursSaved = useCallback(
    (summary: string) => {
      writeFlag('saia-workhours-step-confirmed');
      setWorkhoursConfirmed(true);
      handleSaved(summary);
    },
    [handleSaved, writeFlag]
  );

  const handleLimitsSaved = useCallback(
    (summary: string) => {
      writeFlag('saia-limits-step-confirmed');
      setLimitsConfirmed(true);
      handleSaved(summary);
    },
    [handleSaved, writeFlag]
  );

  // Fertig-Aktionen: Nachricht in den Chat legen und den Takeover schließen
  const queueAndClose = useCallback(
    (message: string) => {
      const st = useAIChatStore.getState();
      st.queueUserMessage(message);
      st.setSetupModalOpen(false);
      st.setContext({ activeSetupStep: undefined });
    },
    []
  );

  return (
    <Box className={cx(classes.workspace, variant === 'modal' && 'modal')}>
      {/* Kopfzeile: Fortschritt + Schritt-Tabs (v2) */}
      <Box className={classes.header}>
        <Box className={cx(classes.headerTop, isModal && classes.centered)}>
          <Typography variant="body2" className={classes.headerTitle}>
            {isDe ? 'Einrichtung' : 'Setup'}
          </Typography>
          <Typography variant="caption" className={classes.readyLabel}>
            {doneCount === stepOrder.length
              ? isDe ? 'startbereit' : 'ready to go'
              : isDe
                ? `${doneCount} von ${stepOrder.length} Schritten vollständig`
                : `${doneCount} of ${stepOrder.length} steps complete`}
          </Typography>
          <Box className={classes.progressTrack}>
            <Box
              className={classes.progressFill}
              style={{ width: `${Math.round((doneCount / stepOrder.length) * 100)}%` }}
            />
          </Box>
        </Box>
        <Box className={cx(classes.tabs, isModal && classes.centered, isModal && classes.tabsWrap)}>
          {stepOrder.map((key, index) => {
            const done = status[key] === true;
            const isActive = activeStep === key;
            return (
              <button
                key={key}
                type="button"
                className={cx(classes.tab, isActive && 'active')}
                onClick={() => setSidePanel(key)}
              >
                <span className={cx(classes.tabDot, done ? 'done' : 'open')}>{done ? '✓' : '!'}</span>
                <span className={classes.tabLabel}>
                  {index + 1}. {stepLabels[key]}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            className={cx(classes.tab, activeStep === 'done' && 'active')}
            onClick={() => setSidePanel(null)}
          >
            <span className={cx(classes.tabDot, allDone ? 'done' : 'open')}>{allDone ? '✓' : '!'}</span>
            <span className={classes.tabLabel}>{isDe ? 'Fertig' : 'Done'}</span>
          </button>
        </Box>
      </Box>

      {/* Inhalt des aktiven Schritts */}
      <Box className={cx(classes.body, isModal && classes.bodyCentered)}>
        {activeStep === 'profile' && (
          <ProfileDataPanel
            variant="workspace"
            onClose={() => setSidePanel(null)}
            onSaved={handleSaved}
            onComplete={goNext}
          />
        )}
        {activeStep === 'team' && (
          <TeamPanel isAdmin={isAdmin} onSaved={handleSaved} confirmed={teamConfirmed} onConfirm={handleTeamConfirm} onNext={goNext} />
        )}
        {activeStep === 'license' && <LicensePanel onSaved={handleSaved} done={status.license === true} onNext={goNext} />}
        {activeStep === 'linkedin' && (
          <LinkedInConnectPanel
            variant="workspace"
            onClose={() => setSidePanel(null)}
            onSubmitted={refresh}
            onComplete={goNext}
          />
        )}
        {activeStep === 'workhours' && <WorkHoursPanel onSaved={handleWorkhoursSaved} onComplete={goNext} />}
        {activeStep === 'limits' && <AccountLimitsPanel onSaved={handleLimitsSaved} onComplete={goNext} />}
        {activeStep === 'signature' && (
          <SignaturePanel
            onSaved={handleSaved}
            onComplete={goNext}
            onSkip={handleSignatureSkip}
          />
        )}
        {activeStep === 'blacklist' && (
          <BlacklistPanel onSaved={handleSaved} onSkip={handleBlacklistSkip} skipped={blacklistSkipped} onNext={goNext} />
        )}
        {activeStep === 'done' && (
          <Box className={classes.doneCard}>
            <span className={classes.doneCheck}>{allDone ? '✓' : '!'}</span>
            <Typography variant="subtitle2" className={classes.doneTitle}>
              {allDone
                ? isDe ? 'Alles startklar' : 'All set'
                : isDe ? 'Fast geschafft' : 'Almost there'}
            </Typography>
            {!allDone && (
              <Typography variant="caption" className={classes.doneText}>
                {isDe
                  ? `Noch offen: ${stepOrder.filter((k) => status[k] === false)
                      .map((k) => stepLabels[k])
                      .join(', ')}. Du kannst trotzdem schon Nutzer anlegen oder eine Kampagne vorbereiten.`
                  : `Still open: ${stepOrder.filter((k) => status[k] === false)
                      .map((k) => stepLabels[k])
                      .join(', ')}. You can still add users or prepare a campaign.`}
              </Typography>
            )}
            <Typography variant="caption" className={classes.doneText}>
              {isDe
                ? 'Account-Daten, LinkedIn, Arbeitszeiten, Limits und Signatur sind eingerichtet. Sag mir im Chat, wenn du deine erste Kampagne erstellen willst.'
                : 'Account details, LinkedIn, working hours, limits and signature are set up. Tell me in the chat when you want to create your first campaign.'}
            </Typography>
            <Box className={classes.doneActions}>
              <button
                type="button"
                className={classes.doneButton}
                onClick={() =>
                  queueAndClose(
                    isDe
                      ? 'Die Einrichtung ist abgeschlossen — lass uns meine erste Kampagne erstellen.'
                      : 'Setup is complete — let’s create my first campaign.'
                  )
                }
              >
                {isDe ? 'Erste Kampagne erstellen' : 'Create first campaign'}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className={classes.doneButtonSecondary}
                  onClick={() =>
                    queueAndClose(
                      isDe
                        ? 'Ich möchte weitere Nutzer für unseren Workspace anlegen. Frag mich nach den nötigen Angaben und lade sie ein.'
                        : 'I want to add more users to our workspace. Ask me for the required details and invite them.'
                    )
                  }
                >
                  {isDe ? 'Weitere Nutzer anlegen' : 'Add more users'}
                </button>
              )}
              {isAdmin && (
                <button
                  type="button"
                  className={classes.doneButtonSecondary}
                  onClick={() =>
                    queueAndClose(
                      isDe
                        ? 'Ich möchte eine übergreifende Blacklist einrichten (Firmen und/oder Kandidaten, die nie kontaktiert werden dürfen). Frag mich zuerst nach der Art der Einträge und nimm dann mehrere Einträge auf einmal entgegen.'
                        : 'I want to set up a workspace-wide blacklist (companies and/or candidates that must never be contacted). Ask me for the entry type first, then accept multiple entries at once.'
                    )
                  }
                >
                  {isDe ? 'Blacklist einrichten' : 'Set up blacklist'}
                </button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  workspace: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: dc.bg,
    borderLeft: `1px solid ${dc.border}`,
    '&.modal': {
      borderLeft: 'none',
      overflow: 'hidden',
    },
  },
  header: {
    flexShrink: 0,
    backgroundColor: dc.surface,
    borderBottom: `1px solid ${dc.border}`,
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '13px 26px 0',
  },
  centered: {
    maxWidth: 1200,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  bodyCentered: {
    alignItems: 'center',
  },
  tabsWrap: {
    // Takeover: Tabs umbrechen statt horizontal scrollen
    flexWrap: 'wrap',
    overflowX: 'visible',
    columnGap: 14,
    rowGap: 0,
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: dc.ink,
    flexShrink: 0,
  },
  readyLabel: {
    fontSize: 12,
    color: dc.muted,
    flexShrink: 0,
  },
  progressTrack: {
    width: 120,
    height: 6,
    borderRadius: 999,
    backgroundColor: '#E9EDF7',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: dc.primary,
    transition: 'width 0.3s ease',
  },
  tabs: {
    display: 'flex',
    gap: 22,
    padding: '12px 26px 0',
    overflowX: 'auto',
  },
  tab: {
    flexShrink: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '4px 0 11px',
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    borderBottom: '2px solid transparent',
    color: dc.faint,
    '&.active': {
      borderBottom: `2px solid ${dc.primary}`,
      color: dc.ink,
    },
    '&:hover': {
      opacity: 0.8,
    },
  },
  tabDot: {
    width: 15,
    height: 15,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontSize: 8.5,
    fontWeight: 700,
    '&.done': {
      backgroundColor: dc.success,
      color: '#fff',
    },
    '&.open': {
      backgroundColor: '#fff',
      color: dc.warn,
      border: `1.5px solid ${dc.warn}`,
    },
  },
  tabLabel: {
    fontSize: 13.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: 'inherit',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '28px 26px 40px',
    scrollbarWidth: 'thin',
    scrollbarColor: '#C0C9E0 transparent',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#C9D2E8',
      borderRadius: 999,
    },
  },
  doneCard: {
    maxWidth: 460,
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  doneCheck: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    backgroundColor: dc.success,
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  doneTitle: {
    fontWeight: 700,
    color: dc.ink,
  },
  doneText: {
    color: dc.muted,
    lineHeight: 1.55,
  },
  doneActions: {
    marginTop: 6,
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  doneButton: {
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.success,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
  },
  doneButtonSecondary: {
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: dc.primary,
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      borderColor: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
}));

export default ChatWorkspace;
